import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import { fmtDec, MetricCard, GC, TC } from '../utils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export default function CommissionSummary({ data }) {
  const { commissionSummary = [], commissionRates = [] } = data;
  const active = commissionSummary.filter(r => r.commission > 0);
  const totalComm = active.reduce((s, r) => s + r.commission, 0);
  const totalPaid = active.reduce((s, r) => s + r.paid, 0);
  const totalDue  = active.reduce((s, r) => s + r.due, 0);

  const earnedData = {
    labels: active.map(r => r.name.split(' ')[0]),
    datasets: [{ data: active.map(r => r.commission), backgroundColor: '#3ea4b1', borderRadius: 5, borderSkipped: false }],
  };
  const statusData = {
    labels: active.map(r => r.name.split(' ')[0]),
    datasets: [
      { label: 'Paid', data: active.map(r => r.paid), backgroundColor: '#1D9E75', borderRadius: [5,5,0,0], borderSkipped: false },
      { label: 'Balance due', data: active.map(r => r.due), backgroundColor: '#E24B4A', borderRadius: [5,5,0,0], borderSkipped: false },
    ],
  };
  const barOpts = (stacked) => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: stacked, labels: { font: { size: 11 }, color: TC } }, tooltip: { callbacks: { label: ctx => '$' + ctx.parsed.y.toFixed(2) } } },
    scales: {
      x: { stacked, grid: { display: false }, ticks: { color: TC, font: { size: 11 } } },
      y: { stacked, grid: { color: GC }, ticks: { color: TC, font: { size: 11 }, callback: v => '$' + (v / 1000).toFixed(1) + 'k' } },
    },
  });

  const getRate = (name) => {
    const r = commissionRates.find(x => x.name.toLowerCase().includes(name.split(' ')[0].toLowerCase()));
    return r ? r.rate + '%' : '0%';
  };
  const getType = (name) => {
    const r = commissionRates.find(x => x.name.toLowerCase().includes(name.split(' ')[0].toLowerCase()));
    return r ? r.type : '—';
  };

  return (
    <div>
      <div className="metrics">
        <MetricCard label="Total commissions earned" value={fmtDec(totalComm)} sub="2026 YTD" />
        <MetricCard label="Total paid out" value={fmtDec(totalPaid)} sub={Math.round(totalPaid / totalComm * 100) + '% of earned'} />
        <MetricCard label="Total balance due" value={fmtDec(totalDue)} sub={active.filter(r => r.due > 0).length + ' rep(s) outstanding'} valueStyle={totalDue > 0 ? { color: 'var(--red)' } : {}} />
        <MetricCard label="Reps on commission" value={active.length} sub={'of ' + commissionSummary.length + ' total'} />
      </div>

      <div className="table-card">
        <div className="table-header"><span className="table-header-title">Commission summary — 2026 YTD</span></div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Rep</th><th>Item total</th><th>Rate</th><th>Type</th><th>Earned</th><th>Paid</th><th>Balance due</th><th>Status</th></tr></thead>
            <tbody>
              {commissionSummary.map(r => (
                <tr key={r.name}>
                  <td style={{ fontWeight: 500 }}>{r.name}</td>
                  <td className="td-money">{fmtDec(r.itemTotal)}</td>
                  <td className="td-num">{getRate(r.name)}</td>
                  <td className="td-num">{getType(r.name)}</td>
                  <td className="td-money" style={{ color: 'var(--green)' }}>{fmtDec(r.commission)}</td>
                  <td className="td-money">{fmtDec(r.paid)}</td>
                  <td className="td-money" style={{ color: r.due > 0 ? 'var(--red)' : 'var(--text3)' }}>{r.due > 0 ? fmtDec(r.due) : '—'}</td>
                  <td>
                    {r.due > 0
                      ? <span className="badge badge-amber">Balance due</span>
                      : r.commission > 0
                      ? <span className="badge badge-green">Fully paid</span>
                      : <span className="badge badge-gray">No commission</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="two-col">
        <div className="chart-card">
          <div className="chart-card-title">Commission earned by rep</div>
          <div className="chart-card-sub">Total commissions 2026 YTD</div>
          <div className="chart-wrap" style={{ height: 220 }}><Bar data={earnedData} options={barOpts(false)} /></div>
        </div>
        <div className="chart-card">
          <div className="chart-card-title">Paid vs. balance due</div>
          <div className="chart-card-sub">Payout status per rep</div>
          <div className="chart-wrap" style={{ height: 220 }}><Bar data={statusData} options={barOpts(true)} /></div>
        </div>
      </div>
    </div>
  );
}
